import React, { useState, useEffect } from 'react';
import { Typography, Tabs, Form, Input, Button, Upload, message, Spin, Card, Space, Tooltip, Alert, Radio, Divider, Progress } from 'antd';
import { UploadOutlined, DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from 'react-query';
import { sequenceService, pollTaskResult } from '../../api/sequenceService';
import { SequenceAnalysisTask } from '@/api/types';
import { ResultDisplay } from '../../components/results';
import { isMultiFastaFormat, parseFasta, batchSequences, isSingleFastaFormat, extractSingleFastaSequence } from '@/utils/sequenceUtils';

const { Title, Text } = Typography;
const { TextArea } = Input;

const formatTaskName = (name: string) => name?.replace(/\s*-\s*统一任务API\s*$/, '') || '';

// 辅助函数：自动检测序列类型
const detectSequenceType = (sequence: string): 'protein' | 'dna' | 'rna' | '' => {
  if (!sequence) return ''; // 默认为空，显示所有功能

  // 1. 清理序列，去除FASTA头和空格
  let cleanSeq = sequence;
  if (cleanSeq.includes('>')) {
    const lines = cleanSeq.split('\n');
    cleanSeq = lines.filter(line => !line.startsWith('>')).join('');
  }
  const seq = cleanSeq.toUpperCase().replace(/\s/g, '');
  if (!seq) return '';

  // 2. 定义核酸字符的正则表达式
  const nucleotideOnlyRegex = /^[ACGTUN\-]+$/;

  // 3. 判断序列是否只由核酸字符组成
  if (nucleotideOnlyRegex.test(seq)) {
    // 如果是，那么它就是DNA或RNA
    const hasU = seq.includes('U');
    const hasT = seq.includes('T');

    if (hasU && hasT) {
      // 如果同时包含T和U，序列不规范，为避免错误分析，默认为蛋白质
      console.log("序列同时包含 'T' 和 'U'，类型不明确，默认为蛋白质");
      return 'protein';
    }
    
    if (hasU) {
      console.log("序列仅由核酸字符组成且包含 'U'，判断为 RNA");
      return 'rna';
    }
    
    // 如果只包含核酸字符且不含U（或只含T），则为DNA
    console.log("序列仅由核酸字符组成且不含 'U'，判断为 DNA");
    return 'dna';
  } else {
    // 如果包含任何其他字符，则判断为蛋白质
    console.log("序列包含非核酸字符，判断为蛋白质");
    return 'protein';
  }
};

// 定义任务执行参数接口
interface TaskExecutionParams {
  taskId: string;
  params: any;
  meta?: {
    originalId?: string;
    sequenceName?: string;
  };
}

export default function SequenceProcessingPage() {
  const [results, setResults] = useState<any[]>([]); // 修改为数组，存储多个结果
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [currentSequence, setCurrentSequence] = useState<string>(''); // 存储当前序列
  const [isBatchProcessing, setIsBatchProcessing] = useState<boolean>(false); // 是否正在批量处理
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 }); // 批量处理进度
  const [form] = Form.useForm();
  
  // 1. 从后端获取可用任务列表
  const { data: tasks, isLoading: isLoadingTasks } = useQuery(
    'sequenceAnalysisTasks', 
    () => sequenceService.getAnalysisTasks(),
    {
      onSuccess: (data) => {
        console.log('成功加载任务列表:', data);
      },
      onError: (error: any) => {
        console.error('加载分析任务失败:', error);
        message.error('加载分析任务失败: ' + error.message);
      }
    }
  );

  // 实时检测输入序列的类型
  const sequenceInput = Form.useWatch('sequence', form);
  const detectedType: 'protein' | 'dna' | 'rna' | '' = detectSequenceType(sequenceInput);
  
  // 添加日志以显示当前检测到的序列类型
  useEffect(() => {
    console.log('当前检测到的序列类型:', detectedType);
  }, [detectedType]);
  
  // 添加日志以显示过滤后的任务列表
  useEffect(() => {
    if (tasks) {
      console.log('原始任务列表:', tasks);
      console.log('任务列表类型:', typeof tasks);
      
      // 检查任务对象的结构
      if (tasks.length > 0) {
        const sampleTask = tasks[0];
        console.log('样例任务对象:', sampleTask);
        console.log('applicable_to类型:', typeof sampleTask.applicable_to);
        console.log('applicable_to值:', sampleTask.applicable_to);
      }
      
      // 尝试不同的过滤方法
      try {
        const filteredTasks = tasks.filter(task => {
          if (!task.applicable_to) {
            console.log(`任务 ${task.id} 没有applicable_to属性`);
            return false;
          }
          
          if (typeof task.applicable_to === 'string') {
            const isApplicable = task.applicable_to === detectedType;
            console.log(`任务 ${task.id} applicable_to是字符串: ${task.applicable_to}, 是否适用: ${isApplicable}`);
            return isApplicable;
          }
          
          if (Array.isArray(task.applicable_to)) {
            const isApplicable = task.applicable_to.includes(detectedType);
            console.log(`任务 ${task.id} applicable_to是数组: [${task.applicable_to.join(',')}], 是否适用: ${isApplicable}`);
            return isApplicable;
          }
          
          console.log(`任务 ${task.id} applicable_to类型未知: ${typeof task.applicable_to}`);
          return false;
        });
        
        console.log('过滤后的任务列表:', filteredTasks);
        console.log('过滤条件 - 当前序列类型:', detectedType);
        
        if (filteredTasks.length === 0) {
          console.log('警告: 没有适用于当前序列类型的任务');
        }
      } catch (error) {
        console.error('过滤任务时出错:', error);
      }
    }
  }, [tasks, detectedType]);

  // 2. 创建一个通用的执行任务的mutation
  const { mutate: executeTask, isLoading: isExecutingTask } = useMutation(
    async (data: TaskExecutionParams) => {
      const response = await sequenceService.executeAnalysisTask(data.taskId, data.params);
      
      // 检查是否是任务管理器返回的异步任务对象
      if (response?.id && response?.status) { // `id` 和 `status` 是任务对象的标志
        const taskId = response.id;
        // 新的异步任务格式 - 轮询获取结果
        message.loading({ content: '任务已提交，正在获取结果...', key: 'polling' });
        try {
          const result = await pollTaskResult(taskId);
          message.success({ content: '分析完成', key: 'polling', duration: 2 });
          return { isPolledResult: true, data: result, taskId: taskId, originalResponse: response };
        } catch (pollError) {
          message.warning({ content: '轮询超时，请稍后在任务管理页面查看结果', key: 'polling', duration: 5 });
          console.warn('轮询超时:', pollError);
          return { isTaskSubmitted: true, taskId: taskId, originalResponse: response };
        }
        // 新的异步任务格式 - 轮询获取结果
        message.loading('任务已提交，正在获取结果...', 0);
        try {
          const result = await pollTaskResult(response.task_id);
          message.destroy(); // 清除loading消息
          return { isPolledResult: true, data: result, taskId: response.task_id, originalResponse: response };
        } catch (pollError) {
          message.destroy();
          console.warn('轮询超时:', pollError);
          return { isTaskSubmitted: true, taskId: response.task_id, originalResponse: response };
        }
      } else {
        // 旧格式或直接结果
        return { isDirectResult: true, data: response, originalResponse: response };
      }
    },
    {
      onSuccess: (result, variables) => {
        console.log("任务执行结果:", result);
        
        if (result.isPolledResult || result.isDirectResult) {
          // 有实际结果数据
          setResults(prevResults => [
            ...prevResults, 
            { 
              data: {
                ...result.data,
                originalId: variables.meta?.originalId
              }, 
              taskId: variables.taskId,
              timestamp: new Date().toISOString(),
              title: tasks?.find(t => t.id === variables.taskId)?.name || '分析结果',
              sequence: variables.params.sequence,
              originalId: variables.meta?.originalId
            }
          ]);
          setCurrentTaskId(variables.taskId);
          message.success('分析完成');
        } else if (result.isTaskSubmitted) {
          // 任务已提交但未获取到结果
          message.success(`任务已提交，任务ID: ${result.taskId}，请在任务管理页面查看结果`);
        }
      },
      onError: (error: any) => {
        message.destroy(); // 清除可能的loading消息
        console.error("任务执行失败:", error);
        message.error('分析失败: ' + (error.response?.data?.detail || error.message));
      },
    }
  );

  // 3. 通用任务提交处理
  const handleTaskSubmit = (taskId: string) => {
    form.validateFields().then(values => {
      const sequence = values.sequence.trim();
      
      // 检查是否是多序列FASTA格式
      if (isMultiFastaFormat(sequence)) {
        // 如果是多序列FASTA格式，使用批量处理函数
        handleMultiSequenceSubmit(taskId, sequence, values);
        return;
      }
      
      // 检查序列是否发生变化
      if (currentSequence && sequence !== currentSequence) {
        // 如果序列变化，询问用户是否要清除之前的结果
        if (results.length > 0 && !window.confirm('您输入了新的序列，是否清除之前的分析结果？')) {
          return; // 用户取消操作
        }
        // 清除之前的结果
        setResults([]);
      }
      
      // 保存当前序列
      setCurrentSequence(sequence);
      
      // 设置当前任务ID
      setCurrentTaskId(taskId);

      // 检查是否是单个FASTA格式序列
      let processedSequence = sequence;
      let originalId = `>序列`;
      let sequenceName = values.name || '序列';
      
      if (isSingleFastaFormat(sequence)) {
        // 提取FASTA序列和ID
        const extractedData = extractSingleFastaSequence(sequence);
        processedSequence = extractedData.sequence;
        originalId = `>${extractedData.id}`;
        sequenceName = extractedData.id || values.name || '序列';
      }

      // 基础参数
      const baseParams = {
        sequence: processedSequence,
        name: sequenceName,
      };

      // 如果序列类型为空，默认为蛋白质
      const effectiveType = detectedType || 'protein';
      
      let params: any = {};
      
      // 根据taskId决定需要哪些参数
      switch (taskId) {
        // 只需要 sequence
        case 'dna_to_rna':
        case 'rna_to_dna':
        case 'dna_reverse_complement':
        case 'rna_reverse_complement':
        case 'convert/dna_to_rna':
        case 'convert/rna_to_dna':
        case 'convert/dna_reverse_complement':
        case 'convert/rna_reverse_complement':
          params = { sequence: baseParams.sequence };
          break;

        // 需要 sequence 和 reverse
        case 'dna_complement':
        case 'rna_complement':
        case 'convert/dna_complement':
        case 'convert/rna_complement':
          params = { sequence: baseParams.sequence, reverse: false };
          break;
        
        // 需要 sequence 和可选的 sequence_type
        case 'validate':
          params = { sequence: baseParams.sequence, sequence_type: effectiveType };
          break;

        // 需要 sequence 和 sequence_type
        case 'nucleotide_to_protein':
        case 'convert/nucleotide_to_protein':
          // 确保只有当序列类型为DNA或RNA时才能翻译为蛋白质
          if (effectiveType === 'dna' || effectiveType === 'rna') {
            params = { sequence: baseParams.sequence, sequence_type: effectiveType };
          } else {
            message.error('只有DNA或RNA序列才能翻译为蛋白质');
            return; // 中止任务执行
          }
          break;

        // 默认情况，大部分任务需要 sequence 和 sequence_type
        default:
          params = { sequence: baseParams.sequence, sequence_type: effectiveType };
          break;
      }

      // 直接使用任务列表中的 handler_name，没有则回退 taskId 本身
      const handlerName = tasks?.find(t => t.id === taskId)?.handler_name || taskId;

      console.log(`为任务 ${taskId} 构建的参数:`, params, '使用处理器:', handlerName);

      // 保存原始ID以便传递给结果
      const originalIdForResult = originalId;
      
      // 修改executeTask的调用，传递额外的信息
      executeTask({ 
        taskId, 
        params: { ...params, handler_name: handlerName },
        meta: {
          originalId: originalIdForResult,
          sequenceName: sequenceName
        }
      });
    }).catch(info => {
      console.log('表单验证失败:', info);
    });
  };
  
  // 多序列批量处理
  const handleMultiSequenceSubmit = async (taskId: string, fastaContent: string, formValues: any) => {
    try {
      // 解析多序列FASTA
      const sequences = parseFasta(fastaContent);
      
      // 检查序列数量限制
      const MAX_SEQUENCES = 1000; // 最大允许的序列数
      if (sequences.length > MAX_SEQUENCES) {
        if (!window.confirm(`检测到${sequences.length}条序列，超过最大限制(${MAX_SEQUENCES})。是否仅处理前${MAX_SEQUENCES}条？`)) {
          return;
        }
        // 截取限制数量的序列
        sequences.splice(MAX_SEQUENCES);
      }
      
      // 批量处理设置
      setIsBatchProcessing(true);
      setBatchProgress({ current: 0, total: sequences.length });
      
      // 清除当前结果，准备新的批量处理
      if (results.length > 0) {
        if (!window.confirm(`检测到${sequences.length}条序列，将开始批量处理。是否清除之前的分析结果？`)) {
          setIsBatchProcessing(false);
          return;
        }
        setResults([]);
      } else {
        message.info(`检测到${sequences.length}条序列，开始批量处理...`);
      }

      // 如果序列类型为空，默认为DNA
      const effectiveType = detectedType || 'dna';

      // 规范化为 batch_* 任务ID，用于结果组件选择
      const normalizeToBatchTaskId = (rawId: string) => {
        let id = (rawId || '').trim().replace(/^\/+|\/+$/g, '');
        // 只取最后一段
        if (id.includes('/')) {
          const parts = id.split('/');
          id = parts[parts.length - 1];
        }
        if (!id.startsWith('batch_')) {
          id = `batch_${id}`;
        }
        return id;
      };

      // 组装批量FASTA内容（保持原始ID，如无则生成一个）
      const fastaText = sequences
        .map((s, i) => {
          const header = s.originalId && s.originalId.startsWith('>')
            ? s.originalId
            : `>${formValues.name || 'Sequence'}_${i + 1}`;
          return `${header}\n${s.sequence}`;
        })
        .join('\n');

      // 构造请求参数：默认传 content；部分任务附带 sequence_type
      const needsType = (() => {
        // validate 和 六框翻译需要指定核酸类型；其他转换类直接 content 即可
        const base = taskId.includes('/') ? taskId.split('/').pop()! : taskId;
        return ['validate', 'nucleotide_to_protein'].includes(base);
      })();

      const params: any = needsType
        ? { content: fastaText, sequence_type: effectiveType }
        : { content: fastaText };

      // 直接使用任务管理器统一执行（后端tasks已提供handler_name）
      const task = tasks?.find(t => (t.id || '').trim() === taskId);
      const response = await sequenceService.executeAnalysisTask(task?.handler_name || taskId, params);

      // 检查响应格式（兼容任务管理器返回 { id } 以及旧格式 { task_id, success }）
      const createdTaskId = (response as any)?.task_id || (response as any)?.id;
      if (createdTaskId) {
        // 新的异步任务格式
        message.loading('任务已提交，正在获取结果...', 0);
        try {
          const result = await pollTaskResult(createdTaskId);
          message.destroy(); // 清除loading消息
          const batchTaskId = normalizeToBatchTaskId(taskId);
          const task = tasks?.find(t => {
            const tid = (t.id || '').trim();
            return tid === taskId || tid.endsWith(`/${taskId}`) || tid.endsWith(taskId);
          });
          const newResult = {
            data: result,
            taskId: batchTaskId,
            timestamp: new Date().toISOString(),
            title: `${task?.name || '批量处理结果'} - 共 ${sequences.length} 条`,
            isBatchResult: true,
            originalInput: fastaText,
          };

          setResults(prev => [...prev, newResult]);
          message.success(`批量处理完成，共 ${sequences.length} 条序列`);
        } catch (pollError) {
          message.destroy();
          message.warning('任务已提交但获取结果超时，请在任务管理页面查看结果');
        }
      } else if (response?.count !== undefined && response?.items) {
        // 旧的直接结果格式（向后兼容）
        const batchTaskId = normalizeToBatchTaskId(taskId);
        const task = tasks?.find(t => {
          const tid = (t.id || '').trim();
          return tid === taskId || tid.endsWith(`/${taskId}`) || tid.endsWith(taskId);
        });
        const newResult = {
          data: response,
          taskId: batchTaskId,
          timestamp: new Date().toISOString(),
          title: `${task?.name || '批量处理结果'} - 共 ${sequences.length} 条`,
          isBatchResult: true,
          originalInput: fastaText,
        };

        setResults(prev => [...prev, newResult]);
        setBatchProgress({ current: sequences.length, total: sequences.length });
        message.success(`批量处理完成，共 ${sequences.length} 条序列`);
      } else {
        // 其他格式，显示任务已提交
        message.success(`批量任务已提交，共 ${sequences.length} 条序列，请在任务管理页面查看结果`);
      }

// ...
    } finally {
      setIsBatchProcessing(false);
      setBatchProgress({ current: 0, total: 0 });
    }
  };
  
  // 删除结果的处理函数
  const handleDeleteResult = (index: number) => {
    setResults(prevResults => prevResults.filter((_, i) => i !== index));
  };

  // 清空所有结果
  const handleClearAllResults = () => {
    if (window.confirm('确定要清空所有分析结果吗？')) {
      setResults([]);
    }
  };
  
  const isLoading = isExecutingTask || isBatchProcessing;

  // 检查是否有 hydropathy 类型的结果
  const hasHydropathyResults = results.some(result => result.taskId === 'hydropathy');

  const analysisTab = (
    <Card>
      <Form form={form} layout="vertical">
        <Form.Item
          name="sequence"
          label="序列"
          rules={[{ required: true, message: '请输入序列' }]}
        >
          <TextArea 
            rows={10} 
            placeholder={"支持格式：\n1. 纯文本序列：直接输入DNA/RNA序列\n示例：ATGCGTACGTAGCTAGCTAG\n\n2. 单序列FASTA：标准FASTA格式\n示例：>gene1\nATGCGTACGTAGCTAGCTAG\n\n3. 多序列FASTA：批量分析\n示例：>gene1\nATGCGTACGTAG\n>gene2\nTAGCTAGCTGAC"}
          />
        </Form.Item>
        
        <Form.Item name="name" label="序列名称（可选，多序列时将作为前缀）">
          <Input placeholder="例如：my_sequence" />
        </Form.Item>
        
        {isBatchProcessing && (
          <div style={{ marginTop: 16 }}>
            <Alert
              message="正在批量处理多序列"
              description={
                <div>
                  <Progress 
                    percent={Math.round((batchProgress.current / batchProgress.total) * 100)} 
                    status="active"
                  />
                  <div style={{ textAlign: 'center' }}>
                    {batchProgress.current} / {batchProgress.total} 条序列已处理
                  </div>
                </div>
              }
              type="info"
              showIcon
            />
          </div>
        )}
        
        <Form.Item label="可用分析任务">
          {isLoadingTasks ? (
            <Spin tip="正在加载任务..." />
          ) : (
            <>
              {!tasks || tasks.length === 0 ? (
                <Alert message="未找到可用任务" type="warning" />
              ) : (
                <>
                  <Space wrap>
                    {tasks
                      .filter(task => {
                        // 当没有检测到序列类型时，显示所有任务
                        if (detectedType === '') return true;
                        
                        // 确保任务对象和applicable_to属性存在
                        if (!task || !task.applicable_to) return false;
                        
                        // 处理不同类型的applicable_to
                        if (typeof task.applicable_to === 'string') {
                          return task.applicable_to === detectedType;
                        }
                        
                        if (Array.isArray(task.applicable_to)) {
                          return task.applicable_to.includes(detectedType);
                        }
                        
                        return false;
                      })
                      .map(task => (
                        <Button
                          key={task.id}
                          onClick={() => handleTaskSubmit(task.id)}
                          loading={isExecutingTask && currentTaskId === task.id}
                          disabled={isExecutingTask}
                        >
                          {formatTaskName(task.name)}
                        </Button>
                      ))}
                  </Space>
                  
                  {detectedType !== '' && tasks.filter(task => {
                    if (!task || !task.applicable_to) return false;
                    
                    if (typeof task.applicable_to === 'string') {
                      return task.applicable_to === detectedType;
                    }
                    
                    if (Array.isArray(task.applicable_to)) {
                      return task.applicable_to.includes(detectedType);
                    }
                    
                    return false;
                  }).length === 0 && (
                    <Alert message={`没有适用于当前序列类型(${detectedType})的任务`} type="info" />
                  )}
                </>
              )}
              <div style={{ marginTop: '10px' }}>
                <Text type="secondary">当前序列类型: {detectedType}</Text>
              </div>
            </>
          )}
        </Form.Item>
      </Form>
    </Card>
  );

  // 渲染结果
  const renderResults = () => {
    // 按任务ID对结果进行分组
    const groupedResults: Record<string, any[]> = results.reduce((acc, result) => {
      const key = result.taskId;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(result);
      return acc;
    }, {});

    // 需要合并显示的转换任务类型
    const conversionTaskIds = [
      'convert/dna_to_rna',
      'convert/rna_to_dna',
      'convert/dna_complement',
      'convert/rna_complement',
      'convert/dna_reverse_complement',
      'convert/rna_reverse_complement'
    ];

    // 收集所有转换结果
    const conversionResults: any[] = [];
    conversionTaskIds.forEach(taskId => {
      if (groupedResults[taskId] && groupedResults[taskId].length > 0) {
        conversionResults.push(...groupedResults[taskId]);
        delete groupedResults[taskId]; // 从分组中移除已处理的结果
      }
    });

    // 存储所有要渲染的结果组件
    const resultElements: JSX.Element[] = [];

    // 如果有转换结果，则合并显示
    if (conversionResults.length > 0) {
      // 按转换类型分组
      const conversionTypeGroups: Record<string, any[]> = conversionResults.reduce((acc, result) => {
        // 提取转换类型，如 dna_to_rna, dna_complement 等
        const conversionType = result.taskId.split('/').pop();
        if (!acc[conversionType]) {
          acc[conversionType] = [];
        }
        acc[conversionType].push(result);
        return acc;
      }, {});

      // 为每种转换类型创建一个合并结果
      Object.entries(conversionTypeGroups).forEach(([conversionType, typeResults]) => {
        if (typeResults.length > 0) {
          // 先按originalIndex排序，确保顺序一致
          typeResults.sort((a, b) => {
            return (a.originalIndex || 0) - (b.originalIndex || 0);
          });
          
          // 获取每个结果的序列数据
          const combinedData = typeResults.map(r => {
            // 确保每个结果数据都包含taskId，用于在SequenceConversionResult中显示功能名称
            return {
              ...r.data,
              taskId: r.taskId, // 将taskId添加到数据中
              originalIndex: r.originalIndex // 保持原始索引
            };
          });
          
          // 格式化标题
          let titleText = conversionType
            .replace('dna_to_rna', 'DNA转录为RNA')
            .replace('rna_to_dna', 'RNA逆转录为DNA')
            .replace('dna_complement', 'DNA互补')
            .replace('rna_complement', 'RNA互补')
            .replace('dna_reverse_complement', 'DNA反向互补')
            .replace('rna_reverse_complement', 'RNA反向互补');
          
          // 添加合并后的转换结果到渲染列表
          resultElements.push(
            <ResultDisplay
              key={`combined-${conversionType}`}
              data={combinedData}
              title={`序列${titleText}结果 (${combinedData.length}条)`}
              taskId={typeResults[0].taskId}
              multiResults={true}
              onDeleteResult={() => {
                // 删除这一类型的所有结果
                setResults(prev => prev.filter(r => !typeResults.includes(r)));
              }}
            />
          );
        }
      });
    }

    // 添加其他未合并的结果
    Object.entries(groupedResults).forEach(([taskId, taskResults]) => {
      // 先按originalIndex排序，确保顺序一致
      taskResults.sort((a, b) => {
        return (a.originalIndex || 0) - (b.originalIndex || 0);
      });
      
      taskResults.forEach((result, index) => {
        // 确保结果数据中包含taskId
        const resultWithTaskId = {
          ...result,
          data: {
            ...result.data,
            taskId: result.taskId
          }
        };
        
        resultElements.push(
          <ResultDisplay
            key={`${taskId}-${index}`}
            {...resultWithTaskId}
            multiResults={true}
            onDeleteResult={() => handleDeleteResult(results.indexOf(result))}
          />
        );
      });
    });

    return resultElements;
  };

  return (
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Typography style={{ marginBottom: 24, textAlign: 'center' }}>
          <Title level={2}>核酸序列分析</Title>
        </Typography>

        {analysisTab}

        {isLoading && (
          <div style={{ textAlign: 'center', margin: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>正在处理中，请稍候...</div>
          </div>
        )}

        {results.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={3}>分析结果</Title>
              <Button type="primary" danger onClick={handleClearAllResults}>
                清空所有结果
              </Button>
            </div>
            
            {renderResults()}
          </div>
        )}
      </div>
  );
}